import React, { useContext, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import NumbersRoundedIcon from '@mui/icons-material/NumbersRounded'
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import CloseIcon from '@mui/icons-material/Close'

import API from '../../../../../services/API'

import StatCard from '../../../../../components/Cards/StatCard'
import { Edit } from '@mui/icons-material'
import BigModal from '../../../../../components/Modals/BigModal'
import AddNewInsurance from './AddNewInsurance'
import { TenantSettingsContext } from '../../../../../App'

const isPdf = (key = '') => String(key).toLowerCase().endsWith('.pdf')

const fileLabel = (key = '') => {
  const ext = String(key).split('.').pop()?.toLowerCase()
  if (!ext) return 'File'
  if (ext === 'pdf') return 'PDF'
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'Image'
  if (['doc', 'docx'].includes(ext)) return 'Document'
  return ext.toUpperCase()
}

const niceDate = (d) => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '—')

const handleFileDownload = async (key) => {
  const res = await API.post('/gets3downloadurl', { key })
  window.open(res.data, '_blank', 'noreferrer')
}

const InsuranceView = ({ insurance, onClose }) => {
  const [edit, setEdit] = useState(false)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const now = useMemo(() => moment(), [])
  const status = useMemo(() => {
    if (!insurance?.dateFrom || !insurance?.dateTo)
      return { label: 'Unknown', color: 'default' }
    const from = moment(insurance.dateFrom)
    const to = moment(insurance.dateTo)
    if (now.isBefore(from)) return { label: 'Not started', color: 'warning' }
    if (now.isAfter(to)) return { label: 'Expired', color: 'error' }
    return { label: 'Active', color: 'success' }
  }, [insurance?.dateFrom, insurance?.dateTo, now])

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        bgcolor: 'background.default',
        border: (t) =>
          `1px solid ${t.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.10)'
            : 'rgba(0,0,0,0.10)'
          }`,
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, bgcolor: 'background.paper' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction="row" alignItems="center" gap={1.5} minWidth={0}>
            <Typography
              variant="title2"
              sx={{ fontWeight: 900, letterSpacing: -0.3 }}
              noWrap
            >
              Insurance Policy
            </Typography>

            <Chip
              label={status.label}
              color={status.color}
              sx={{ fontWeight: 800, borderRadius: 1 }}
            />
          </Stack>

          <Stack direction='row' gap={1} alignItems='center'>
            <IconButton size='small' onClick={() => setEdit(true)}><Edit /></IconButton>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </Stack>
        </Stack>
      </Box>

      <Divider sx={{ opacity: 0.6 }} />

      {/* Body */}
      <Box sx={{ p: 3, maxHeight: '75vh', overflowY: 'auto' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={NumbersRoundedIcon}
              label="Policy Number"
              value={insurance?.policyNo ?? '—'}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={BusinessRoundedIcon}
              label="Provider"
              value={insurance?.provider ?? '—'}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={EventAvailableRoundedIcon}
              label="Valid From"
              value={niceDate(insurance?.dateFrom)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={EventBusyRoundedIcon}
              label="Valid Until"
              value={niceDate(insurance?.dateTo)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={PaidRoundedIcon}
              label="Cost"
              value={
                insurance?.cost != null
                  ? `${orgSettings?.currency ?? '£'}${Number(insurance.cost).toFixed(2)}`
                  : '—'
              }
            />
          </Grid>

          {/* Documents */}
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: (t) =>
                  t.palette.mode === 'dark'
                    ? 'none'
                    : '0 2px 10px rgba(0,0,0,0.04)',
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Typography
                  variant="overline"
                  sx={{ opacity: 0.65, letterSpacing: 0.8 }}
                >
                  Documents
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.65 }}>
                  {insurance?.documents?.length
                    ? `${insurance.documents.length} file(s)`
                    : 'No files'}
                </Typography>
              </Stack>

              <Stack gap={1.25} sx={{ mt: 1.5 }}>
                {insurance?.documents?.length ? (
                  insurance.documents.map((key) => (
                    <Paper
                      key={key}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                      }}
                    >
                      <Stack
                        direction="row"
                        gap={1.25}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                      >
                        {isPdf(key) ? (
                          <PictureAsPdfRoundedIcon fontSize="small" />
                        ) : (
                          <VisibilityRoundedIcon fontSize="small" />
                        )}
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 800 }}
                          noWrap
                        >
                          {fileLabel(key)}
                        </Typography>
                      </Stack>

                      <Stack direction="row" gap={1} flex="0 0 auto">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<DownloadRoundedIcon />}
                          color="anMainBlue"
                          onClick={() => handleFileDownload(key)}
                        >
                          Download
                        </Button>
                      </Stack>
                    </Paper>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.7, fontStyle: 'italic' }}
                  >
                    No insurance documents uploaded.
                  </Typography>
                )}
              </Stack>
            </Paper>
            <Grid xs={12} sx={{ mt: 1.5 }}>
              {insurance?.updatedAt && (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.7 }}
                  noWrap
                  textAlign="end"
                >
                  Updated by {insurance?.updatedBy?.firstName}{' '}
                  {insurance?.updatedBy?.lastName} at{' '}
                  {moment(insurance.updatedAt).format(
                    'HH:mm [on] Do MMM YY',
                  )}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <BigModal open={edit} onClose={() => setEdit(false)}>
        <AddNewInsurance existingInsurance={insurance} onClose={() => setEdit(false)} />
      </BigModal>
    </Paper>
  )
}

export default InsuranceView
